import { useState, useMemo } from 'react';

interface AnalyteItem {
  code: string;
  compound: string;
  color: string;
}

interface Props {
  analytes: AnalyteItem[];
  visibleCodes: Set<string>;
  onToggle: (code: string) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
}

export function AnalyteSelectorPanel({ analytes, visibleCodes, onToggle, onSelectAll, onDeselectAll }: Props) {
  const [search, setSearch] = useState('');
  const showSearch = analytes.length > 10;

  const filtered = useMemo(() => {
    const sorted = [...analytes].sort((a, b) => a.compound.toLowerCase().localeCompare(b.compound.toLowerCase()));
    if (!search) return sorted;
    const q = search.toLowerCase();
    return sorted.filter(a =>
      a.compound.toLowerCase().includes(q) || a.code.toLowerCase().includes(q)
    );
  }, [analytes, search]);

  const allVisible = visibleCodes.size === analytes.length;
  const noneVisible = visibleCodes.size === 0;

  return (
    <div className="border border-mono-800 rounded p-3 bg-mono-900/30 space-y-2">
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-mono-500 tracking-wider mr-1">ANALYTES:</span>
          <button
            onClick={onSelectAll}
            disabled={allVisible}
            className="px-2 py-0.5 text-[9px] tracking-wider text-mono-400 hover:text-mono-200 bg-mono-800 rounded disabled:opacity-30 transition-colors"
          >
            ALL
          </button>
          <button
            onClick={onDeselectAll}
            disabled={noneVisible}
            className="px-2 py-0.5 text-[9px] tracking-wider text-mono-400 hover:text-mono-200 bg-mono-800 rounded disabled:opacity-30 transition-colors"
          >
            NONE
          </button>
        </div>
        {showSearch && (
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search analytes..."
            className="w-full sm:w-36 sm:ml-auto px-2 py-0.5 text-[10px] bg-mono-800 border border-mono-700 rounded text-mono-200 placeholder-mono-600 outline-none focus:border-mono-500"
          />
        )}
      </div>
      <div className="flex flex-wrap gap-1 max-h-[120px] overflow-y-auto" role="listbox" aria-label="Analyte selection">
        {filtered.map((a) => {
          const visible = visibleCodes.has(a.code);
          return (
            <button
              key={a.code}
              role="option"
              aria-selected={visible}
              onClick={() => onToggle(a.code)}
              className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] tracking-wider transition-colors ${
                visible
                  ? 'bg-mono-800 text-mono-200 hover:bg-mono-700'
                  : 'bg-mono-900 text-mono-600 hover:text-mono-400'
              }`}
            >
              <span
                className="inline-block w-2 h-2 rounded-full flex-shrink-0"
                style={{
                  backgroundColor: visible ? a.color : '#444',
                }}
              />
              {a.compound}
            </button>
          );
        })}
      </div>
    </div>
  );
}
